import React from 'react'
import { uuid } from 'uuidv4'
import moment from 'moment'
import Col from 'antd/lib/col'
import Row from 'antd/lib/row'
import Button from 'antd/lib/button'
import Switch from 'antd/lib/switch'
import Tooltip from 'antd/lib/tooltip'
import { PlusCircleOutlined, CloseCircleOutlined } from '@ant-design/icons'
import { HourPicker } from 'components/Widgets'
import { DayOfWeek, SplitHour } from '../types'
import { GetShiftLengthInSeconds, ValidateShift } from '../utils'
import { StyledDatePicker } from 'components/Common'
import { includes } from 'lodash'
interface SpecialDayProps {
  dateStamp: string
  shifts: SplitHour[]
  disabled: boolean
  usedDates: string[]
  updateSpecialDayShifts: (dateStamp: string, newShifts: SplitHour[]) => void
  removeDatedShifts: (dateStamp: string) => void
}

const TIME_FORMAT = 'HH:mm'
const DAY_NAME_FORMAT = 'dddd'
const DEFAULT_SHIFT_SHAPE = {
  day_of_week: 'monday',
  start_time: '08:00:00',
  seconds_length: 3600,
  enabled: true,
  special_date: null,
  closed: false
}

const SpecialDay = ({
  dateStamp,
  shifts,
  disabled,
  usedDates,
  updateSpecialDayShifts,
  removeDatedShifts
}: SpecialDayProps) => {
  const dayShifts = shifts
  const dayOfWeek = moment(dateStamp).format(DAY_NAME_FORMAT).toLowerCase()
  const isAddShiftDisabled = disabled || dateStamp === 'Invalid date'

  const addNewShift = () => {
    const newDayShifts = [
      ...dayShifts,
      {
        ...DEFAULT_SHIFT_SHAPE,
        day_of_week: dayOfWeek,
        special_date: dateStamp
      }
    ]
    updateSpecialDayShifts(dateStamp, newDayShifts as SplitHour[])
  }

  const updateShiftStart = (startTime: string, index: number) => {
    const newDayShifts = [...dayShifts]
    newDayShifts[index].start_time = startTime ? `${startTime}:00` : '08:00:00'
    updateSpecialDayShifts(dateStamp, newDayShifts)
  }

  const updateShiftDuration = (
    endTime: string,
    startTime: string,
    index: number
  ) => {
    const shiftLength = GetShiftLengthInSeconds(startTime, endTime)
    const newDayShifts = [...dayShifts]

    if (newDayShifts[index]) {
      newDayShifts[index].seconds_length = shiftLength
      updateSpecialDayShifts(dateStamp, newDayShifts)
    }
  }

  const removeShift = (index: number) => {
    const newDayShifts = [...dayShifts]
    if (newDayShifts[index]) {
      newDayShifts.splice(index, 1)
      updateSpecialDayShifts(dateStamp, newDayShifts)
    }
  }

  const toggleShiftClosure = (value: boolean) => {
    const newDayShifts = [...dayShifts].map((shift: SplitHour) => {
      shift.closed = !value
      return shift
    })
    updateSpecialDayShifts(dateStamp, newDayShifts)
  }

  const changeDate = (date: moment.Moment | null) => {
    const formatted_date = moment(date).format('YYYY-MM-DD')

    const newDayShifts = [...dayShifts].map((shift: SplitHour) => {
      shift.special_date = formatted_date
      shift.day_of_week = moment(date).format('dddd').toLowerCase() as DayOfWeek
      return shift
    })

    updateSpecialDayShifts(formatted_date, newDayShifts)
  }

  const datepickerDisabledDate = (current: moment.Moment) =>
    current.isBefore(moment()) ||
    includes(usedDates, current.format('yyyy-MM-DD'))

  const openForBusiness = dayShifts.find(({ closed }: SplitHour) => !closed)

  return (
    <Row className='_mb-32' align='top' data-testid='split-hours-day-row'>
      <Col span={12}>
        <Row gutter={16} align='middle'>
          <Col>
            <Switch
              defaultChecked={!!openForBusiness}
              onChange={toggleShiftClosure}
              disabled={disabled}
            />
          </Col>
          <Col>
            <Row
              align='middle'
              className='_mb-0'
              data-testid='split-hours-day-row-day-name'
            >
              <StyledDatePicker
                format='DD/MM/YYYY'
                onChange={changeDate}
                defaultValue={
                  dateStamp === 'Invalid date' ? undefined : moment(dateStamp)
                }
                disabledDate={datepickerDisabledDate}
              />
            </Row>
          </Col>
        </Row>
      </Col>
      {!openForBusiness && (
        <Col span={12}>
          <Row gutter={[8, 0]} className='_mb-4'>
            <Col span={11}>
              <Row className='_mb-0' align='middle'>
                Not operating on this day
              </Row>
            </Col>
            <Col span={11} />
            <Col span={2}>
              <Button
                icon={<CloseCircleOutlined />}
                disabled={disabled}
                type='link'
                data-testid='split-hours-day-row-remove'
                className='-primary -plain'
                onClick={() => removeDatedShifts(dateStamp)}
              />
            </Col>
          </Row>
        </Col>
      )}
      {openForBusiness && (
        <Col span={12}>
          {dayShifts.map((shift: SplitHour, index: number) => {
            const { start_time, seconds_length } = shift
            const classNames = ValidateShift(
              shift,
              dayShifts,
              {} as Record<DayOfWeek, SplitHour[]>,
              dayOfWeek as DayOfWeek,
              index
            )
              ? ''
              : '-error'
            return (
              <Row key={uuid()} gutter={[8, 0]} className='_mb-4'>
                <Col span={11}>
                  <HourPicker
                    classNames={classNames}
                    dataTestId='split-hours-day-row-start'
                    disabled={disabled}
                    value={moment(start_time, TIME_FORMAT).format(TIME_FORMAT)}
                    onChange={(value: string) => updateShiftStart(value, index)}
                  />
                </Col>
                <Col span={11}>
                  <HourPicker
                    classNames={classNames}
                    dataTestId='split-hours-day-row-end'
                    disabled={disabled}
                    value={moment(start_time, TIME_FORMAT)
                      .add(seconds_length, 's')
                      .format(TIME_FORMAT)}
                    onChange={(value: string) =>
                      updateShiftDuration(value, start_time, index)
                    }
                  />
                </Col>
                <Col span={2}>
                  <Button
                    icon={<CloseCircleOutlined />}
                    disabled={disabled}
                    type='link'
                    data-testid='split-hours-day-row-remove'
                    className='-primary -plain'
                    onClick={() => removeShift(index)}
                  />
                </Col>
              </Row>
            )
          })}
          <Row align='top' className='_mb-0'>
            <Col>
              {isAddShiftDisabled ? (
                <Tooltip
                  placement='right'
                  title={'Please select a date first.'}
                >
                  <Button
                    icon={<PlusCircleOutlined />}
                    type='link'
                    size='small'
                    disabled={isAddShiftDisabled}
                    className='-primary'
                    style={{ textTransform: 'capitalize' }}
                    data-testid='split-hours-day-row-add'
                    onClick={addNewShift}
                  >
                    Add shift
                  </Button>
                </Tooltip>
              ) : (
                <Button
                  icon={<PlusCircleOutlined />}
                  type='link'
                  size='small'
                  disabled={isAddShiftDisabled}
                  className='-primary'
                  data-testid='split-hours-day-row-add'
                  onClick={addNewShift}
                >
                  Add shift
                </Button>
              )}
            </Col>
          </Row>
        </Col>
      )}
    </Row>
  )
}

export default SpecialDay
